import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Footer } from '../components/Footer';
import { toast } from 'sonner';
import { Mail, KeyRound, ArrowLeft, RefreshCw } from 'lucide-react';
import authService from '../services/authService';
import subscriptionService from '../services/subscriptionService';
import logo from '../Logo.svg';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function LoginPage({ setIsAuthenticated }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState('password'); // password | otp-email | otp-code

  const [formData, setFormData] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});

  const [otpEmail, setOtpEmail] = useState('');
  const [otpEmailError, setOtpEmailError] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpCodeError, setOtpCodeError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const validate = (data) => {
    const e = {};
    if (!data.email.trim()) e.email = 'Email is required.';
    else if (!EMAIL_RE.test(data.email.trim())) e.email = 'Enter a valid email address.';
    if (!data.password) e.password = 'Password is required.';
    return e;
  };

  const handleChange = (e) => {
    const updated = { ...formData, [e.target.name]: e.target.value };
    setFormData(updated);
    if (errors[e.target.name]) setErrors(prev => ({ ...prev, [e.target.name]: undefined }));
  };

  const handleBlur = (e) => {
    const errs = validate(formData);
    if (errs[e.target.name]) setErrors(prev => ({ ...prev, [e.target.name]: errs[e.target.name] }));
  };

  const finishLogin = async (response) => {
    setIsAuthenticated(true);
    if (response.roleName === 'Admin') {
      toast.success(`Welcome back, ${response.name}!`);
      navigate('/admin/users');
      return;
    }
    const subscriptionStatus = await subscriptionService.checkSubscriptionStatus();
    if (subscriptionStatus.status === 'ACTIVE') {
      toast.success(`Welcome back, ${response.name}!`);
      navigate('/dashboard');
    } else {
      if (subscriptionStatus.status === 'EXPIRED') {
        toast.warning('Your subscription has expired. Please renew to continue.');
      } else {
        toast.info('Please complete your subscription to access the dashboard.');
      }
      navigate('/subscription');
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(formData);
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setIsLoading(true);
    try {
      const response = await authService.login(formData.email, formData.password);
      await finishLogin(response);
    } catch (error) {
      toast.error(error || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!otpEmail.trim()) { setOtpEmailError('Email is required.'); return; }
    if (!EMAIL_RE.test(otpEmail.trim())) { setOtpEmailError('Enter a valid email address.'); return; }
    setOtpEmailError('');
    setIsLoading(true);
    try {
      await authService.sendOtp(otpEmail.trim());
      toast.success('Login code sent! Check your email.');
      setMode('otp-code');
    } catch (error) {
      toast.error(error || 'Failed to send login code.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otpCode.trim()) { setOtpCodeError('Please enter the 6-digit code.'); return; }
    if (!/^\d{6}$/.test(otpCode.trim())) { setOtpCodeError('Code must be 6 digits.'); return; }
    setOtpCodeError('');
    setIsLoading(true);
    try {
      const response = await authService.verifyOtp(otpEmail.trim(), otpCode.trim());
      await finishLogin(response);
    } catch (error) {
      toast.error(error || 'Invalid or expired code.');
    } finally {
      setIsLoading(false);
    }
  };

  const inputCls = (hasError) => hasError ? 'border-destructive focus-visible:ring-destructive' : '';

  return (
    <div className="min-h-screen gradient-hero flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
        <Card className="w-full max-w-md animate-scale-in">
          <CardHeader className="space-y-4">
            <div className="flex justify-center">
              <Link to="/">
                <img src={logo} alt="Verdionix" className="h-14 w-auto" style={{ mixBlendMode: 'multiply' }} />
              </Link>
            </div>
            <div className="text-center">
              <CardTitle className="text-2xl font-display">Welcome Back</CardTitle>
              <CardDescription>
                {mode === 'password' && 'Sign in to your Verdionix account'}
                {mode === 'otp-email' && 'Enter your email to receive a one-time login code'}
                {mode === 'otp-code' && `Enter the 6-digit code sent to ${otpEmail}`}
              </CardDescription>
            </div>
          </CardHeader>

          <CardContent>
            {mode === 'password' && (
              <form onSubmit={handlePasswordSubmit} className="space-y-4" noValidate>
                <div className="space-y-1">
                  <Label htmlFor="email">Email <span className="text-destructive">*</span></Label>
                  <Input id="email" name="email" type="email" placeholder="you@company.com"
                    value={formData.email} onChange={handleChange} onBlur={handleBlur}
                    className={inputCls(errors.email)} />
                  {errors.email && <p className="text-xs text-destructive mt-0.5">{errors.email}</p>}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="password">Password <span className="text-destructive">*</span></Label>
                  <Input id="password" name="password" type="password" placeholder="••••••••"
                    value={formData.password} onChange={handleChange} onBlur={handleBlur}
                    className={inputCls(errors.password)} />
                  {errors.password && <p className="text-xs text-destructive mt-0.5">{errors.password}</p>}
                </div>
                <div className="flex items-center justify-end">
                  <Link to="/forgot-password" className="text-sm text-primary hover:underline">Forgot password?</Link>
                </div>
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? 'Signing in...' : 'Sign In'}
                </Button>
                <div className="relative my-1">
                  <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">or</span>
                  </div>
                </div>
                <Button type="button" variant="outline" className="w-full" onClick={() => setMode('otp-email')}>
                  <Mail className="w-4 h-4 mr-2" />
                  Login with Email Code (OTP)
                </Button>
              </form>
            )}

            {mode === 'otp-email' && (
              <form onSubmit={handleSendOtp} className="space-y-4" noValidate>
                <div className="space-y-1">
                  <Label htmlFor="otpEmail">Email <span className="text-destructive">*</span></Label>
                  <Input id="otpEmail" type="email" placeholder="you@company.com"
                    value={otpEmail}
                    onChange={(e) => { setOtpEmail(e.target.value); if (otpEmailError) setOtpEmailError(''); }}
                    onBlur={() => {
                      if (!otpEmail.trim()) setOtpEmailError('Email is required.');
                      else if (!EMAIL_RE.test(otpEmail.trim())) setOtpEmailError('Enter a valid email address.');
                    }}
                    className={inputCls(otpEmailError)} />
                  {otpEmailError && <p className="text-xs text-destructive mt-0.5">{otpEmailError}</p>}
                </div>
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? 'Sending...' : <><Mail className="w-4 h-4 mr-2" />Send Login Code</>}
                </Button>
                <Button type="button" variant="ghost" className="w-full" onClick={() => setMode('password')}>
                  <ArrowLeft className="w-4 h-4 mr-2" />Back to Password Login
                </Button>
              </form>
            )}

            {mode === 'otp-code' && (
              <form onSubmit={handleVerifyOtp} className="space-y-4" noValidate>
                <div className="space-y-1">
                  <Label htmlFor="otpCode">6-Digit Code <span className="text-destructive">*</span></Label>
                  <Input id="otpCode" type="text" inputMode="numeric" maxLength={6}
                    placeholder="000000" value={otpCode}
                    onChange={(e) => { setOtpCode(e.target.value.replace(/\D/g, '')); if (otpCodeError) setOtpCodeError(''); }}
                    className={`text-center text-2xl tracking-[0.5em] font-mono ${inputCls(otpCodeError)}`} />
                  {otpCodeError && <p className="text-xs text-destructive mt-0.5">{otpCodeError}</p>}
                  <p className="text-xs text-muted-foreground">Code expires in 10 minutes.</p>
                </div>
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? 'Verifying...' : <><KeyRound className="w-4 h-4 mr-2" />Verify and Sign In</>}
                </Button>
                <Button type="button" variant="ghost" className="w-full text-sm" disabled={isLoading}
                  onClick={() => { setOtpCode(''); setOtpCodeError(''); setMode('otp-email'); }}>
                  <RefreshCw className="w-4 h-4 mr-2" />Resend or change email
                </Button>
              </form>
            )}

            <div className="mt-6 text-center text-sm">
              <span className="text-muted-foreground">{"Don't have an account? "}</span>
              <Link to="/register" className="text-primary hover:underline font-medium">Sign up</Link>
            </div>
            <div className="mt-4 text-center">
              <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition">{"← Back to Home"}</Link>
            </div>
          </CardContent>
        </Card>
      </div>
      <Footer />
    </div>
  );
}
